package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.serialization.json.Json
import kotlinx.serialization.encodeToString

import com.vidaensupalabra.vsp.utils.getFromLocalStorage
import com.vidaensupalabra.vsp.utils.saveToLocalStorage
import com.vidaensupalabra.vsp.datos.PlaylistItem
import com.vidaensupalabra.vsp.datos.YouTubeRepository
import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente50
import com.vidaensupalabra.vsp.ui.theme.White

// Constantes de Configuración
private const val PLAYLIST_ID = "PLCaej2DhSMNs2pLnfQV7SFsnlmwiWhmvE"

@Composable
fun MusicaScreen() {
    var canciones by remember { 
        mutableStateOf<List<PlaylistItem>?>(
            getFromLocalStorage("yt_playlist_$PLAYLIST_ID")?.let {
                try { Json.decodeFromString(it) } catch (e: Exception) { null }
            }
        ) 
    }
    var currentlyPlayingVideoId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(PLAYLIST_ID) {
        try {
            val fetchedCanciones = YouTubeRepository.getPlaylistItems(PLAYLIST_ID)
            if (fetchedCanciones != null && fetchedCanciones != canciones) {
                canciones = fetchedCanciones
                if (fetchedCanciones.isNotEmpty()) {
                    saveToLocalStorage("yt_playlist_$PLAYLIST_ID", Json.encodeToString(fetchedCanciones))
                }
            }
            
            // Si falló la red y no hay historial de canciones en LocalStorage (entorno nuevo)
            if (fetchedCanciones == null && canciones == null) {
                canciones = emptyList() // Desactiva el "Cargando..." y pinta el EmptyState
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("WARN: Fallo asíncrono capturado en MusicaScreen -> ${e.message}")
        }
    }

    // Contenedor principal scrollable (reemplazo de LazyColumn)
    Div(attrs = {
        style {
            width(100.percent)
            minHeight(100.vh)
            backgroundColor(VspBase)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            fontFamily("sans-serif")
            boxSizing("border-box")
            padding(16.px)
        }
    }) {
        Div(attrs = {
            style {
                width(100.percent)
                maxWidth(800.px) // Restringir ancho en pantallas grandes
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
            }
        }) {
            MusicaHeader()

            when {
                canciones == null -> LoadingState()
                canciones?.isEmpty() == true -> EmptyState()
                else -> {
                    // Bucle directo sobre los elementos del DOM (Reemplazo de itemsIndexed)
                    canciones.orEmpty().forEach { cancion ->
                        SongCard(
                            cancion = cancion,
                            onClick = { currentlyPlayingVideoId = cancion.videoId }
                        )
                    }
                }
            }
            
            Div(attrs = { style { height(32.px) } }) // Margen inferior
        }
    }

    currentlyPlayingVideoId?.let { videoId ->
        VideoPlayerModal(videoId = videoId, onDismiss = { currentlyPlayingVideoId = null })
    }
}

@Composable
private fun MusicaHeader() {
    H2(attrs = {
        style {
            color(White)
            fontWeight("bold")
            textAlign("center")
            width(100.percent)
            marginTop(16.px)
            marginBottom(8.px)
            fontSize(24.px)
        }
    }) { Text("Canciones para este Domingo") }

    P(attrs = {
        style {
            color(White)
            textAlign("center")
            width(100.percent)
            marginBottom(24.px)
            fontSize(16.px)
            lineHeight("1.5")
        }
    }) { Text("Prepárate para nuestro servicio escuchando y aprendiendo las canciones que cantaremos juntos.") }
}

@Composable
private fun LoadingState() {
    Div(attrs = {
        style {
            width(100.percent)
            height(200.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            color(VspMarco)
            fontWeight("bold")
            fontSize(18.px)
        }
    }) {
        Text("Cargando canciones...")
    }
}

@Composable
private fun EmptyState() {
    P(attrs = {
        style {
            color(White)
            padding(32.px)
            textAlign("center")
            fontSize(16.px)
        }
    }) {
        Text("No se encontraron canciones en la lista. Intenta de nuevo más tarde.")
    }
}

@Composable
private fun SongCard(cancion: PlaylistItem, onClick: () -> Unit) {
    Div(attrs = {
        style {
            width(100.percent)
            backgroundColor(VspMarcoTransparente50)
            borderRadius(16.px)
            property("box-shadow", "0px 4px 8px rgba(0,0,0,0.2)")
            marginBottom(16.px)
            padding(16.px)
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        H3(attrs = {
            style {
                color(White)
                fontWeight("bold")
                textAlign("center")
                width(100.percent)
                marginBottom(12.px)
                marginTop(0.px)
                fontSize(18.px)
                // Estilos para evitar desbordamiento de texto largo
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            }
        }) { Text(cancion.title) }
        
        // Contenedor de la miniatura interactiva
        Div(attrs = {
            onClick { onClick() }
            style {
                width(100.percent)
                property("aspect-ratio", "16 / 9")
                borderRadius(8.px)
                property("overflow", "hidden")
                position(Position.Relative)
                cursor("pointer")
            }
        }) {
            YouTubeThumbnail(videoId = cancion.videoId)
            
            // Overlay oscuro y botón Play
            Div(attrs = {
                style {
                    position(Position.Absolute)
                    top(0.px); left(0.px)
                    width(100.percent); height(100.percent)
                    backgroundColor(rgba(0, 0, 0, 0.4))
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                }
            }) {
                Span(attrs = { style { fontSize(64.px); color(White) } }) { Text("▶") }
                Div(attrs = { style { height(8.px) } })
                Span(attrs = { style { color(White); fontSize(16.px) } }) { Text("Toca para reproducir") }
            }
        }
    }
}

@Composable
private fun VideoPlayerModal(videoId: String, onDismiss: () -> Unit) {
    // Overlay fijo pantalla completa
    Div(attrs = {
        style { position(Position.Fixed); top(0.px); left(0.px); width(100.vw); height(100.vh);
            backgroundColor(rgba(0, 0, 0, 0.8)); property("z-index", 1000); display(DisplayStyle.Flex);
            justifyContent(JustifyContent.Center); alignItems(AlignItems.Center) }
    }) {
        // Caja modal interna
        Div(attrs = {
            style { width(90.percent); maxWidth(800.px); backgroundColor(VspBase); borderRadius(16.px)
                padding(16.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center); boxSizing("border-box") }
        }) {
            Div(attrs = {
                style { width(100.percent); property("aspect-ratio", "16 / 9"); borderRadius(8.px); property("overflow", "hidden") }
            }) {
                YouTubeVideoPlayer(videoId = videoId, autoplay = true)
            }
            
            Div(attrs = { style { height(16.px) } })
            
            Button(attrs = {
                onClick { onDismiss() }
                style { padding(10.px, 24.px); backgroundColor(VspMarco); color(White); border(0.px)
                    borderRadius(8.px); cursor("pointer"); fontSize(16.px) }
            }) { Text("Cerrar") }
        }
    }
}